import { useCallback, useEffect, useRef } from "react";
import { useBlocker } from "@tanstack/react-router";
import { Button } from "../../design/components/Button";
import { Dialog } from "../../design/components/Dialog";
import { Reason } from "../../design/components/Reason";
import { useT, type Translate } from "../../i18n/locale";

// Le parti comuni delle bozze delle sezioni di M2 («Campi della scheda», «Chi vede i Lead»), come nel mock-up
// `regole.html` e come «Calendario, fuso e lingua» di M1b: chi esce con una bozza conferma prima (D9), e «Salva…» apre
// «Cosa cambia per il team» con le modifiche a parole e il pulsante che salva per tutta l'azienda.

/** Un messaggio con il testo calcolato quando si mostra: segue la lingua dell'interfaccia. */
export type Feedback = { text: (t: Translate) => string };

type SearchedLocation = { pathname: string; search: unknown };

/**
 * Ferma l'uscita dalla pagina (un'altra sezione, un'altra pagina, un'altra azienda, «Esci») finché c'è una bozza; lo
 * stesso indirizzo non è un'uscita. `beforeunload` vale per il ricaricamento e la chiusura della scheda del browser.
 */
export function useLeaveGuard(dirty: boolean) {
  const dirtyNow = useRef(false);
  useEffect(() => {
    dirtyNow.current = dirty;
  });
  const shouldBlockFn = useCallback(
    ({ current, next }: { current: SearchedLocation; next: SearchedLocation }) =>
      dirtyNow.current && !(current.pathname === next.pathname && JSON.stringify(current.search) === JSON.stringify(next.search)),
    [],
  );
  const enableBeforeUnload = useCallback(() => dirtyNow.current, []);
  return useBlocker({ shouldBlockFn, enableBeforeUnload, withResolver: true });
}

type ConfirmProps = {
  changes: readonly string[];
  error: Feedback | null;
  saving: boolean;
  onBack: () => void;
  onApply: () => void;
};

/** «Cosa cambia per il team»: le modifiche, la nota sul registro, e il rifiuto del server se il salvataggio si è fermato. */
export function ConfirmChangesDialog({ changes, error, saving, onBack, onApply }: ConfirmProps) {
  const t = useT();
  return (
    <Dialog
      open
      size="wide"
      title={t("settings.draft.confirmTitle")}
      // Mentre la richiesta parte non si torna indietro: la finestra si chiude con l'esito.
      onClose={() => {
        if (!saving) onBack();
      }}
      footer={
        <>
          <Button onClick={onBack} disabled={saving}>
            {t("settings.draft.back")}
          </Button>
          <Button variant="primary" onClick={onApply} disabled={saving || changes.length === 0} aria-busy={saving ? "true" : undefined}>
            {t("settings.draft.apply")}
          </Button>
        </>
      }
    >
      <ul>
        {changes.map((sentence) => (
          <li key={sentence}>{sentence}</li>
        ))}
      </ul>
      <p>{t("settings.draft.confirmNote")}</p>
      {error ? (
        <Reason tone="warning" role="alert">
          {error.text(t)}
        </Reason>
      ) : null}
    </Dialog>
  );
}

/** «Non hai salvato le modifiche»: «Resta qui» tiene la bozza, «Esci senza salvare» la perde. */
export function LeaveDialog({ onStay, onLeave }: { onStay: () => void; onLeave: () => void }) {
  const t = useT();
  return (
    <Dialog
      open
      title={t("settings.draft.leaveTitle")}
      onClose={onStay}
      footer={
        <>
          <Button onClick={onLeave}>{t("settings.draft.leaveDiscard")}</Button>
          <Button variant="primary" onClick={onStay}>
            {t("settings.draft.stay")}
          </Button>
        </>
      }
    >
      <p>{t("settings.draft.leaveText")}</p>
    </Dialog>
  );
}

/** Il testo della barra della bozza: quante modifiche ci sono, e che per il team non cambia niente finché non si salva. */
export function draftSummary(t: Translate, count: number): string {
  return count === 1 ? t("settings.draft.summaryOne") : t("settings.draft.summary", { count });
}
